import React, { useState, useEffect, useRef, ReactElement, useCallback, useMemo } from 'react';
import imagesLoaded from 'imagesloaded';
import styled, { CSSObject } from '@emotion/styled';
import _isEmpty from 'lodash/isEmpty';
import { renderAvatar, tsSince } from '../../common';
import { getBackgroundTheme, getBorders, getTextColor } from '../../style';
import Button from '../../atoms/buttons/Button';
import MessageBubble from '../../atoms/mail/MessageBubble';
import ConfirmationModal from '../modals/ConfirmationModal';
import Loader from '../../atoms/LoaderStyled';
import Avatar from '../../atoms/Avatar';
import { getBrowserDetails, isDarkMode, triggerTracking } from '../../utils/helpers';
import 'emoji-mart/css/emoji-mart.css';
import '../../style/customGifEmojiModal.css';
import { GENERAL_MESSAGE_AVATAR } from '../../config/Master';
import { browser } from 'detectrtc';
import { useStateCallback } from '../../utils/customHooks';
import { sendSufficientCredsUnlockAction, sendInsufficientCredsUnlockAction } from '../../actions/purchaseActions';
import useMixPanel from '../../hooks/useMixPanel';
import ConversationActions from './components/ConversationActions';
import ConversationHeader from './components/ConversationHeader';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  handleDeleteImage,
  handleReportImage,
  handleUnlockOffer,
  setSendPoke,
  triggerThreadsFetching,
} from '../../actions/mailActions';
import { REPORT_MESSAGE } from '../../constants/conversation';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { localMessageConversations } from '../../utils/storedLocalData';
import useMailFetchActions from './hooks/useMailFetchActions';
import breakpoints from '../../theme/material/breakpoints';
import useMandatoryWall from '../../hooks/useMandatoryWall';
import { CustomStyledProps } from 'style/style';
import { isMobile } from 'react-device-detect';

const iOSnotPWA = getBrowserDetails().isMobile && getBrowserDetails().isSafari && !getBrowserDetails().isStandaloneMode;
const isChromeIos = browser.isChrome && getBrowserDetails().isIpad;
const isAndroidDevice = getBrowserDetails().showAndroidInstall;

const ConversationContainer = styled('div')(
  (): CSSObject => ({
    height: '100%',
    [breakpoints.up(769)]: {
      background: getBackgroundTheme().block2,
      borderRadius: '0px 10px 10px 0px',
    },
    [breakpoints.down(768.05)]: {
      margin: isAndroidDevice ? '-5px 10px 0 10px' : '10px 10px 0 10px',
      borderRadius: '10px 10px 0 0',
    },
  })
);

const PokeContainer = styled('div')(() => ({
  margin: '0 auto',
  width: '50%',
  color: getTextColor().LIGHT_GRAY.color,
}));

const StyledConversationBody = styled('div')<{ isLocked?: boolean; showPwaInstallPromptBanner?: boolean }>(
  (): CSSObject => ({
    overflow: 'hidden',
    overflowY: 'scroll',
    paddingTop: 10,
    [breakpoints.up(768.05)]: {
      border: isDarkMode() ? '1px solid #404040' : '1px solid #DAE1E9',
      borderRadius: 10,
      margin: '0 20px',
      background: getBackgroundTheme().block,
    },
  }),
  ({ isLocked, showPwaInstallPromptBanner }) => ({
    height: `calc(${getBrowserDetails().isIpad && screen.height > 1024 ? '87vh' : '98vh'} - ${isLocked ? '232px' : '290px'} - 49px)`,
    marginBottom: isLocked ? '20px !important' : 0,
    [breakpoints.down(768.05)]: {
      height: iOSnotPWA
        ? `calc(98vh - ${isLocked ? 0 : showPwaInstallPromptBanner ? '250px' : '185px'})`
        : `calc(100vh - 110px)`,
      background: getBackgroundTheme().block,
    },
  })
);

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const Overview = styled('div')(() => ({
  textAlign: 'center',
  padding: '20px 0',
  borderBottom: getBorders().DEFAULT,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 10,
  alignItems: 'center',
}));

const OfferAccepted = styled('span')(() => ({
  fontSize: getBrowserDetails().isMobile ? 16 : 18,
  color: !isDarkMode() ? '#2490DE' : '#71B5E4',
  fontWeight: 'bold',
}));

const OfferIncentive = styled('span')(
  (): CSSObject => ({
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: getTextColor().LIGHT_GRAY.color,
  })
);

const Messages = styled('div')([
  (): CSSObject => ({
    paddingBottom: isChromeIos ? 100 : 40,
    position: 'relative',
    margin: isMobile ? '20px 10px' : '30px 20px',
  }),
  ({ isIos, isStandaloneMode, initialMsgSize }: CustomStyledProps): CSSObject => ({
    height: initialMsgSize ? 'max-content' : '100%',
    [breakpoints.down(639.05)]: {
      paddingBottom: isIos && !isStandaloneMode ? 100 : !isIos && isStandaloneMode ? 35 : 70,
    },
  }),
]);

const NewMessageNotification = styled('div')({
  width: '45%',
  margin: '10px auto 0',

  [breakpoints.down(768.05)]: {
    width: '80vw',
  },
});

const ReminderContainer = styled('div')({
  textAlign: 'center',
  marginBottom: 20,
});

const WarningMessage = styled('div')(
  (): CSSObject => ({
    fontStyle: 'italic',
    fontSize: 12,
    padding: '10px 20px',
    marginBottom: 20,
    textAlign: 'justify',
    color: getTextColor().LIGHT_GRAY.color,
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'center',
  })
);

const MessageOverlay = styled('div')(
  {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#000000',
    zIndex: 2,
    opacity: '0.7',
  },
  ({ pickerOpen }: CustomStyledProps) => ({
    display: pickerOpen ? 'block' : 'none',
  })
);

const MobileFixSpacer = styled('div')({
  height: getBrowserDetails().isMobile
    ? getBrowserDetails().isStandaloneMode
      ? 150
      : getBrowserDetails().isIpad && screen.height > 1024
        ? 30
        : 260
    : 30,
  position: 'relative',
});

const setCorrectUnlockingAction = (e, unlockStatus) => {
  switch (unlockStatus) {
    case 'sufficient':
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      sendSufficientCredsUnlockAction(e);
      break;
    case 'insufficient':
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      sendInsufficientCredsUnlockAction(e);
      break;
    default:
      return;
  }
};

interface ConversationProps {
  showPwaInstallPromptBanner: boolean;
}

const Conversation = ({ showPwaInstallPromptBanner }: ConversationProps): ReactElement => {
  const dispatch = useDispatch();
  const { fetchConversation } = useMailFetchActions();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { mixpanelData, resetMixpanelData } = useMixPanel();
  const { showMandatoryWall } = useMandatoryWall();

  const conversation = useSelector((state: RootState) => state.mail.conversation);
  const authUser = useSelector((state: RootState) => state.profile);
  const isUploading = useSelector((state: RootState) => state.profile.pendingUploads);
  const isFetchingConversation = useSelector((state: RootState) => state.mail.fetchingConversation);

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [deleteImgModal, setDeleteImgModal] = useState(false);
  const [deleteFor, setDeleteFor] = useState('');
  const [messageID, setMessageID] = useStateCallback('');
  const [reportImgModal, setReportImgModal] = useState(false);
  const [disablePoke, setDisablePoke] = useState(false);
  const [lastMessage, setLastMessage] = useState({});
  const [messagesCollection, setMessagesCollection] = useState([]);

  const conversationRef = useRef(null);
  const sufficientCredits = useRef(null);
  const insufficientCredits = useRef(null);

  const { isMobile, isIos, isStandaloneMode } = getBrowserDetails();
  const isAttractive = authUser && authUser.profile && authUser.profile.data.account_type === 'Attractive';
  const showOverlay = true;
  const uploadInProgress = isUploading && isUploading.public;

  const otherProfile = useMemo(
    () => (conversation?.other_profile && conversation?.other_profile.data) ?? {},
    [conversation?.other_profile]
  );
  const otherAccount = (otherProfile.account && otherProfile.account.data) || {};
  const avatar = (otherProfile.avatar && otherProfile.avatar.data.urls) || {};
  const memoisedOtherProfileThumbAvatar = useMemo(() => renderAvatar(avatar, true), [avatar]);

  const messages = useMemo(() => conversation?.messages, [conversation?.messages]);
  const pokes = useMemo(() => conversation?.pokes, [conversation?.pokes]);
  const messagesPagination = useMemo(() => (messages && messages?.meta?.pagination) || {}, [messages]);
  const isLocked = useMemo(() => conversation.locked, [conversation.locked]);
  const isAdmin = useMemo(() => conversation.admin, [conversation.admin]);

  const offerData = useMemo(() => (conversation?.offer && conversation?.offer?.data) ?? {}, [conversation?.offer]);
  const currency = (offerData.currency_original && offerData.currency_original.data) || {};
  const price = `${currency.initial}${String.fromCharCode(currency.label_unicode)}${offerData.price_original}`;

  const { trackPackagesPageVisited, trackProfileView } = useMixPanel();

  useEffect(() => {
    if (messages && messages.data) {
      setMessagesCollection(messages.data);
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    const scrollNow = () => {
      const scrollHeight = conversationRef && conversationRef.current && conversationRef.current.scrollHeight;

      if (scrollHeight) conversationRef.current.scrollTop = scrollHeight ? scrollHeight : 0;
    };

    if (messagesCollection && messagesCollection.length > 1) {
      scrollNow();
    }
  }, [messagesCollection]);

  useEffect(() => {
    scrollToBottom();
    setDisablePoke(pokes && pokes.can_send_poke);
    sufficientCredits.current = document.getElementById('sufficientCredits');
    insufficientCredits.current = document.getElementById('insufficientCredits');
    triggerTracking('sufficient', sufficientCredits, setCorrectUnlockingAction);
    triggerTracking('insufficient', insufficientCredits, setCorrectUnlockingAction);
  }, [pokes, scrollToBottom]);

  useEffect(() => {
    if (!isFetchingConversation && messagesPagination.current_page && messagesPagination.current_page > 1) {
      setTimeout(() => {
        const convEl = document.getElementById('conversation_body');

        if (convEl && !_isEmpty(lastMessage)) {
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          const lastEl = document.getElementById(lastMessage.id);

          if (lastEl) {
            convEl.scrollTo(0, lastEl?.offsetTop ?? 0);
          }
        }
      }, 300);
    }
  }, [isFetchingConversation, messagesPagination, lastMessage]);

  useEffect(() => {
    const convEl = conversationRef?.current;

    const handleImagesLoaded = () => {
      if (convEl) {
        const lastMessage = messagesCollection[messagesCollection.length - 1];
        const lastEl = document.getElementById(lastMessage?.id ?? null);

        if (lastEl) {
          const bottomPosition = isMobile ? (lastEl?.offsetTop ?? convEl.scrollHeight) : convEl.scrollHeight;

          convEl.scrollTo(0, bottomPosition);
        }
      }
    };

    if (messagesCollection.length > 0 && convEl && messagesPagination?.current_page === 1) {
      //  initially set scroll to bottom based on scroll height

      convEl.scrollTo(0, convEl.scrollHeight);
      const imgLoad = imagesLoaded(convEl);

      // Wait for images to be loaded
      imgLoad.on('always', handleImagesLoaded);
    }
  }, [conversationRef?.current, messagesCollection, isMobile]);

  const handleLoadOldMessage = () => {
    const currentPage = messagesPagination.current_page || 0;

    setLastMessage([...messagesCollection].shift() || {});
    fetchConversation(currentPage + 1);
  };

  const handlePoke = hashId => {
    setDisablePoke(true);
    dispatch(setSendPoke(hashId));
  };

  const canSendPokes = pokes => {
    const canSendPokeInFuture = pokes.max_pokes - pokes.pokes_count > 0;

    return !(pokes.can_send_poke && canSendPokeInFuture && disablePoke);
  };

  const renderAttractiveLockedNotice = () => {
    const isFinalPoke = pokes.max_pokes - pokes.pokes_count === 1;
    const canSendPokeInFuture = pokes.max_pokes - pokes.pokes_count > 0;

    return (
      <PokeContainer>
        <p style={{ marginBottom: 0 }}>
          To ensure genuine interest, {otherAccount.username} has to spend money to unlock this conversation.
        </p>
        {canSendPokeInFuture && (
          <React.Fragment>
            <Button
              disabled={canSendPokes(pokes)}
              onClick={() => handlePoke(otherAccount.hash_id)}
              buttonType="primaryRed"
              customStyle={{ marginTop: 10 }}
            >
              Send a Reminder
            </Button>
            {pokes.can_send_poke && <p>{`You can send a ${isFinalPoke ? 'final' : ''}  reminder after 48 hours.`}</p>}
          </React.Fragment>
        )}
      </PokeContainer>
    );
  };

  const handleOnClickUnlock = () => {
    setUnlockModalOpen(true);
  };

  const handleDeleteImg = useCallback((deleteFor, messageID) => {
    setDeleteImgModal(true);
    setDeleteFor(deleteFor);
    setMessageID(messageID);
  }, []);

  const confirmDelete = () => {
    setDeleteImgModal(false);

    dispatch(handleDeleteImage(REPORT_MESSAGE, messageID, deleteFor, navigate, location, conversation.uid));
  };

  const handleReportImg = useCallback(messageID => {
    setReportImgModal(true);
    setMessageID(messageID);
  }, []);

  const confirmReportImg = () => {
    setReportImgModal(false);
    setMessageID('', () => {
      dispatch(handleReportImage(REPORT_MESSAGE, messageID, navigate, location));
    });
  };

  const getConversationAfterUnlock = async () => {
    const currentHashId = location.pathname.split('/')[2];
    const conversationMessage = await localMessageConversations(currentHashId);

    return _isEmpty(conversationMessage) ? {} : conversationMessage;
  };

  const handleDispatchUnlockOffer = () => {
    const currentConversation = getConversationAfterUnlock();

    dispatch(
      handleUnlockOffer(
        currentConversation,
        conversation.id ?? conversation.uid,
        otherAccount.hash_id,
        params,
        navigate,
        location,
        mixpanelData[EVENT_NAMES.CONVERSATION_UNLOCKED].triggerToBuy,
        () => {
          resetMixpanelData(EVENT_NAMES.CONVERSATION_UNLOCKED);
          dispatch(triggerThreadsFetching(1, navigate, location, null));
        }
      )
    );
  };

  const handleGoToProfile = hashId => {
    trackProfileView({ Source: 'Conversations', User: hashId });
    navigate(`/profile/${hashId}`);
  };

  const renderGenerousActions = () => {
    let actionButton = (
      <Button id="sufficientCredits" onClick={handleOnClickUnlock} buttonType="primaryRed">
        Unlock for {offerData.credit_cost} credits
      </Button>
    );

    // not enough credits so we redirect the user to billing page
    if (offerData.credit_cost > authUser.credits) {
      actionButton = (
        <Button
          id="insufficientCredits"
          to="/packages"
          buttonType="primaryRed"
          onClick={() => {
            trackPackagesPageVisited({ Source: 'Unlock Attempt' });
          }}
        >
          Unlock for {offerData.credit_cost} credits
        </Button>
      );
    }

    return (
      <React.Fragment>
        {actionButton}

        <ConfirmationModal
          header={'Unlock Offer'}
          description={`Do you want to unlock this date for ${offerData.credit_cost} credits?`}
          isOpen={unlockModalOpen}
          closeOnOverlayClick={true}
          cancelText={'Cancel'}
          confirmText={'Unlock'}
          onClose={() => setUnlockModalOpen(false)}
          onConfirm={() => {
            showMandatoryWall(
              () => {
                handleDispatchUnlockOffer();
              },
              () => undefined,
              'Unlock Offer'
            );
          }}
        />
        <NewMessageNotification>
          {messages && messages.data.length > 0 && (
            <React.Fragment>
              {otherAccount.username} sent you <b>{messages.data.length}</b>{' '}
              {messages.data.length > 1 ? 'messages' : 'message'}!{' '}
            </React.Fragment>
          )}
          Use credits to unlock the conversation and set up your date!
        </NewMessageNotification>
      </React.Fragment>
    );
  };

  const renderWarningMessage = () => {
    const userHasSentMessage = messagesCollection.find(message => message.hash_id === authUser.hash_id);

    return (
      (isLocked || !userHasSentMessage) && (
        <WarningMessage>
          Never send or ask for money or gift cards before meeting. Never share or request banking information. Report
          anyone who does.
        </WarningMessage>
      )
    );
  };

  return (
    <React.Fragment>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ReactModalPortal { z-index: 9999; position: relative; }`,
        }}
      />
      <ConversationContainer>
        <ConversationHeader dataTestId="conversation-header" />

        <StyledConversationBody
          id="conversation_body"
          isLocked={isLocked}
          ref={conversationRef}
          showPwaInstallPromptBanner={showPwaInstallPromptBanner}
        >
          {isLocked && (
            <Overview>
              <Avatar
                onClick={() => handleGoToProfile(otherAccount.hash_id)}
                src={renderAvatar(avatar, true)}
                width={72}
                customStyle={{
                  cursor: 'pointer',
                }}
              />
              <OfferAccepted>Offer Accepted</OfferAccepted>
              <OfferIncentive>First date incentive {price}</OfferIncentive>

              {otherProfile.account_type === 'Generous' ? renderAttractiveLockedNotice() : renderGenerousActions()}
            </Overview>
          )}
          <Messages isIos={isIos} isStandaloneMode={isStandaloneMode} initialMsgSize={messagesCollection.length < 7}>
            {isMobile && <MessageOverlay pickerOpen={!isMobile && showOverlay}>&nbsp;</MessageOverlay>}
            {messagesPagination.current_page < messagesPagination.total_pages && !isFetchingConversation && (
              <Button onClick={() => handleLoadOldMessage()} style={{ width: '100%', marginBottom: 10 }}>
                Load older...
              </Button>
            )}
            {isFetchingConversation && (
              <Loader active={true} background="#fff" style={{ width: '100%', height: 100 }} />
            )}
            {isLocked &&
              pokes &&
              pokes.data.reverse().map((poke, index) => (
                <ReminderContainer key={poke.id}>
                  <p style={{ marginBottom: 0 }}>
                    <i>
                      {`${poke.hash_id === authUser.hash_id ? 'You' : otherAccount.username} sent a ${
                        pokes.max_pokes === index + 1 ? 'final' : ''
                      } reminder about this date.`}
                    </i>
                  </p>
                  <span>
                    <small>{tsSince(poke.created_at.date)}</small>
                  </span>
                </ReminderContainer>
              ))}
            {(!isLocked || !!isAttractive) &&
              messagesCollection.map(message => (
                <MessageBubble
                  isBlurred={message.isBlurred}
                  handleDeleteImg={handleDeleteImg}
                  handleReportImg={handleReportImg}
                  key={`${message.uid ?? message.id} - ${message.hash_id}`}
                  isRead={authUser.hash_id === message.hash_id && message.they_read}
                  isSent={authUser.hash_id === message.hash_id}
                  avatar={isAdmin ? GENERAL_MESSAGE_AVATAR : memoisedOtherProfileThumbAvatar}
                  scrollToBottom={scrollToBottom}
                  urls={message.urls}
                  id={message.id ?? message.uid}
                  {...message}
                  isAdminMessage={isAdmin}
                  threadUid={conversation.uid}
                  uid={message.uid}
                />
              ))}
            {renderWarningMessage()}
            {/* message bottom margin fix */}
            <MobileFixSpacer />
          </Messages>
        </StyledConversationBody>

        {!isAdmin && !isLocked && (
          <ConversationActions
            uploadInProgress={uploadInProgress}
            scrollToBottom={scrollToBottom}
            username={otherAccount.username}
            otherAccountHashId={otherAccount.hash_id}
            setMessagesCollection={setMessagesCollection}
            thread={conversation}
          />
        )}
      </ConversationContainer>

      <ConfirmationModal
        contentLabel="Delete Modal"
        isOpen={deleteImgModal}
        description={`Are you sure you want to delete this for ${deleteFor === 'self' ? 'yourself' : 'both'}?`}
        cancelText="Nevermind"
        confirmText="Delete"
        onClose={() => setDeleteImgModal(false)}
        onConfirm={confirmDelete}
      />

      <ConfirmationModal
        contentLabel="Report Modal"
        isOpen={reportImgModal}
        description={
          'Are you sure you want to report this photo message? This user will be blocked from you after reporting.'
        }
        cancelText="Nevermind"
        confirmText="Report"
        onClose={() => setReportImgModal(false)}
        onConfirm={confirmReportImg}
      />
    </React.Fragment>
  );
};

export default React.memo(Conversation);
